import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";

export type InvoiceStatus = "issued" | "paid" | "cancelled";

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  issued: "Issued",
  paid: "Paid",
  cancelled: "Cancelled",
};

export interface RevenueTarget {
  id: string;
  year: number;
  quarter: number | null;
  target_value: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  company_id: string | null;
  invoice_date: string;
  value: string;
  status: InvoiceStatus;
  paid_date: string | null;
  notes: string | null;
  freeagent_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface QuarterSummary {
  quarter: number;
  target: string;
  issued_value: string;
  paid_value: string;
  issued_count: number;
  paid_count: number;
}

export interface YearSummary {
  year: number;
  annual_target: string;
  annual_issued: string;
  annual_paid: string;
  quarters: QuarterSummary[];
}

// --- Targets ---

export function useRevenueTargets(year?: number) {
  const params = year !== undefined ? `?year=${year}` : "";
  return useQuery<RevenueTarget[]>({
    queryKey: ["finance-targets", year],
    queryFn: async () => {
      const res = await apiClient.get<RevenueTarget[]>(`/finance/targets${params}`);
      return res.data;
    },
  });
}

export function useCreateTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      year: number;
      quarter: number | null;
      target_value: number;
      notes?: string;
    }) => {
      const res = await apiClient.post<RevenueTarget>("/finance/targets", data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance-targets"] });
      qc.invalidateQueries({ queryKey: ["finance-summary"] });
    },
  });
}

export function useUpdateTarget(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { target_value?: number; notes?: string }) => {
      const res = await apiClient.put<RevenueTarget>(`/finance/targets/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance-targets"] });
      qc.invalidateQueries({ queryKey: ["finance-summary"] });
    },
  });
}

export function useDeleteTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/finance/targets/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance-targets"] });
      qc.invalidateQueries({ queryKey: ["finance-summary"] });
    },
  });
}

// --- Invoices ---

export function useInvoices(filters?: { year?: number; status?: InvoiceStatus }) {
  const params = new URLSearchParams();
  if (filters?.year !== undefined) params.set("year", String(filters.year));
  if (filters?.status) params.set("status", filters.status);
  return useQuery<Invoice[]>({
    queryKey: ["finance-invoices", filters],
    queryFn: async () => {
      const res = await apiClient.get<Invoice[]>(`/finance/invoices?${params.toString()}`);
      return res.data;
    },
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post<Invoice>("/finance/invoices", data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance-invoices"] });
      qc.invalidateQueries({ queryKey: ["finance-summary"] });
    },
  });
}

export function useUpdateInvoice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.put<Invoice>(`/finance/invoices/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance-invoices"] });
      qc.invalidateQueries({ queryKey: ["finance-summary"] });
    },
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/finance/invoices/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance-invoices"] });
      qc.invalidateQueries({ queryKey: ["finance-summary"] });
    },
  });
}

// --- Summary ---

export function useFinanceSummary(year: number) {
  return useQuery<YearSummary>({
    queryKey: ["finance-summary", year],
    queryFn: async () => {
      const res = await apiClient.get<YearSummary>(`/finance/summary?year=${year}`);
      return res.data;
    },
  });
}
